import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Add,
  Business,
  Person,
  Phone,
  Email,
  LocationOn,
  LocalShipping,
  Delete
} from '@mui/icons-material';

import customerService from '../../services/customerService';
import {
  Customer,
  CustomerAddress,
  CustomerShipmentHistory,
  CreateAddressDto,
  CustomerStatus
} from '../../types/customer';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const addressValidationSchema = yup.object({
  address_line1: yup.string().required('Address line 1 is required'),
  address_line2: yup.string().default(''),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  postal_code: yup.string().required('Postal code is required'),
  country: yup.string().required('Country is required'),
  is_default: yup.boolean().default(false)
});

type AddressFormData = {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  is_default: boolean;
};

const CustomerDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [shipmentHistory, setShipmentHistory] = useState<CustomerShipmentHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(null);
  const [editAddressDialogOpen, setEditAddressDialogOpen] = useState(false);
  const [updatingAddress, setUpdatingAddress] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetAddressForm,
    setValue
  } = useForm<AddressFormData>({
    resolver: yupResolver(addressValidationSchema),
    defaultValues: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      is_default: false
    }
  });

  // Load customer data
  useEffect(() => {
    if (!id) return;

    const loadCustomerData = async () => {
      try {
        setLoading(true);
        setError(null);

        const customerId = parseInt(id);
        
        // Load customer details first (critical)
        try {
          const customerData = await customerService.getCustomer(customerId);
          setCustomer(customerData);
          // Note: Addresses will be fetched separately when backend endpoint is available
          setAddresses([]);
        } catch (error: any) {
          console.error('Error loading customer data:', error);
          const errorMessage = error.response?.data?.message || error.message || t('customers.messages.loadError');
          setError(errorMessage);
          enqueueSnackbar(errorMessage, { variant: 'error' });
          return; // Exit early if customer data fails
        }

        // Load shipment history separately (non-critical)
        try {
          const shipmentData = await customerService.getCustomerShipments(customerId);
          setShipmentHistory(shipmentData);
        } catch (error: any) {
          console.error('Error loading customer shipments:', error);
          // Don't set error state - just show empty shipments with a warning
          setShipmentHistory(null);
          enqueueSnackbar(t('customers.messages.shipmentsLoadError') || 'Failed to load shipments', { variant: 'warning' });
        }
      } catch (error: any) {
        // This catch block should not be reached now, but kept for safety
        console.error('Unexpected error:', error);
        setError('An unexpected error occurred');
        enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadCustomerData();
  }, [id, t, enqueueSnackbar]);

  // Handle add address
  const onAddressSubmit: SubmitHandler<AddressFormData> = async (data) => {
    if (!customer) return;

    try {
      setAddingAddress(true);
      
      const addressData: CreateAddressDto = {
        address_line1: data.address_line1,
        address_line2: data.address_line2 || undefined,
        city: data.city,
        state: data.state,
        postal_code: data.postal_code,
        country: data.country,
        is_default: data.is_default
      };

      const newAddress = await customerService.addCustomerAddress(customer.user_id, addressData);
      setAddresses(prev => [...prev, newAddress]);
      setAddressDialogOpen(false);
      resetAddressForm();
      enqueueSnackbar(t('customers.messages.addressAdded'), { variant: 'success' });
    } catch (error: any) {
      console.error('Error adding address:', error);
      const errorMessage = error.response?.data?.message || error.message || t('customers.messages.addressAddError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setAddingAddress(false);
    }
  };

  // Handle edit address
  const handleEditAddress = (address: CustomerAddress) => {
    setEditingAddress(address);
    // Pre-fill form with address data
    setValue('address_line1', address.address_line.split(', ')[0] || '');
    setValue('address_line2', address.address_line.split(', ')[1] || '');
    setValue('city', address.city);
    setValue('state', address.region);
    setValue('postal_code', address.postal_code);
    setValue('country', address.country);
    setValue('is_default', address.is_default);
    setEditAddressDialogOpen(true);
  };

  // Handle update address submission
  const handleUpdateAddress: SubmitHandler<AddressFormData> = async (data) => {
    if (!customer || !editingAddress) return;
    
    try {
      setUpdatingAddress(true);
      
      const addressData: CreateAddressDto = {
        address_line1: data.address_line1,
        address_line2: data.address_line2 || undefined,
        city: data.city,
        state: data.state,
        postal_code: data.postal_code,
        country: data.country,
        is_default: data.is_default
      };

      const updatedAddress = await customerService.updateCustomerAddress(customer.user_id, editingAddress.id, addressData);
      setAddresses(prev => prev.map(addr => addr.id === editingAddress.id ? updatedAddress : addr));
      setEditAddressDialogOpen(false);
      setEditingAddress(null);
      resetAddressForm();
      enqueueSnackbar(t('customers.messages.addressUpdated'), { variant: 'success' });
    } catch (error: any) {
      console.error('Error updating address:', error);
      const errorMessage = error.response?.data?.message || error.message || t('customers.messages.addressUpdateError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setUpdatingAddress(false);
    }
  };

  // Handle delete address
  const handleDeleteAddress = async (addressId: number) => {
    if (!customer) return;
    
    try {
      setDeletingAddressId(addressId);
      await customerService.deleteCustomerAddress(customer.user_id, addressId);
      setAddresses(prev => prev.filter(addr => addr.id !== addressId));
      enqueueSnackbar(t('customers.messages.addressDeleted'), { variant: 'success' });
    } catch (error: any) {
      console.error('Error deleting address:', error);
      const errorMessage = error.response?.data?.message || error.message || t('customers.messages.addressDeleteError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setDeletingAddressId(null);
    }
  };

  // Get status color
  const getStatusColor = (status: CustomerStatus): 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'warning';
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !customer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('customers.messages.notFound')}
        </Alert>
        <Button onClick={() => navigate('/customers')}>
          {t('common.back')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/customers')}
            sx={{ mr: 2 }}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4" component="h1">
            {customer.company_name}
          </Typography>
          <Chip
            label={t(`customers.status.${customer.status}`)}
            color={getStatusColor(customer.status)}
            sx={{ ml: 2 }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/customers/${customer.user_id}/edit`)}
        >
          {t('common.edit')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('customers.sections.companyInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" alignItems="center" mb={1}>
                <Person sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>{t('customers.fields.contactPerson')}:</strong> {customer.contact_person}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" mb={1}>
                <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>{t('customers.fields.phone')}:</strong> {customer.phone}
                </Typography>
              </Box>
              
              {customer.alternate_phone && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    <strong>{t('customers.fields.alternatePhone')}:</strong> {customer.alternate_phone}
                  </Typography>
                </Box>
              )}
              
              <Box display="flex" alignItems="center" mb={1}>
                <Email sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>{t('customers.fields.email')}:</strong> {customer.email}
                </Typography>
              </Box>
              

              

            </CardContent>
          </Card>
        </Grid>

        {/* Customer Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('customers.sections.statistics')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {customer.total_shipments}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('customers.fields.totalShipments')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {formatCurrency(customer.total_spent)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('customers.fields.totalSpent')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {customer.last_shipment && (
                <Box mt={2} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('customers.fields.lastShipment')}:</strong>{' '}
                    {formatDate(customer.last_shipment)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Addresses */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('customers.sections.addresses')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setAddressDialogOpen(true)}
                >
                  {t('customers.addAddress')}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {addresses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('customers.noAddresses')}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {addresses.map((address) => (
                    <Grid item xs={12} md={6} key={address.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                {address.is_default && (
                                  <Chip
                                    label={t('customers.defaultAddress')}
                                    size="small"
                                    color="primary"
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {customerService.formatAddress(address)}
                              </Typography>
                            </Box>
                            {user?.role === 'admin' && (
                              <Box display="flex" gap={1}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditAddress(address)}
                                  disabled={updatingAddress}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAddress(address.id)}
                                  disabled={deletingAddressId === address.id}
                                  color="error"
                                >
                                  {deletingAddressId === address.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Delete fontSize="small" />
                                  )}
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Shipment History */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('customers.sections.shipmentHistory')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {!shipmentHistory || shipmentHistory.shipments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('customers.noShipments')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('shipments.fields.trackingNumber')}</TableCell>
                        <TableCell>{t('shipments.fields.origin')}</TableCell>
                        <TableCell>{t('shipments.fields.destination')}</TableCell>
                        <TableCell>{t('shipments.fields.status')}</TableCell>
                        <TableCell>{t('shipments.fields.createdAt')}</TableCell>
                        <TableCell align="right">{t('common.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shipmentHistory.shipments.slice(0, 10).map((shipment: any) => (
                        <TableRow key={shipment.id}>
                          <TableCell>{shipment.tracking_number}</TableCell>
                          <TableCell>{shipment.origin}</TableCell>
                          <TableCell>{shipment.destination}</TableCell>
                          <TableCell>
                            <Chip
                              label={t(`shipments.status.${shipment.status}`)}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>{formatDate(shipment.created_at)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('common.view')}>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/shipments/${shipment.tracking_number}`)}
                              >
                                <LocalShipping fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Address Dialog */}
      <Dialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onAddressSubmit)}>
          <DialogTitle>{t('customers.addAddress')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              
              <Grid item xs={12}>
                <TextField
                  {...register('address_line1')}
                  label={t('customers.fields.addressLine1')}
                  fullWidth
                  error={!!errors.address_line1}
                  helperText={errors.address_line1?.message}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  {...register('address_line2')}
                  label={t('customers.fields.addressLine2')}
                  fullWidth
                  error={!!errors.address_line2}
                  helperText={errors.address_line2?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('city')}
                  label={t('customers.fields.city')}
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('state')}
                  label={t('customers.fields.state')}
                  fullWidth
                  error={!!errors.state}
                  helperText={errors.state?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('postal_code')}
                  label={t('customers.fields.postalCode')}
                  fullWidth
                  error={!!errors.postal_code}
                  helperText={errors.postal_code?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('country')}
                  label={t('customers.fields.country')}
                  fullWidth
                  error={!!errors.country}
                  helperText={errors.country?.message}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddressDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={addingAddress}
              startIcon={addingAddress ? <CircularProgress size={20} /> : <Add />}
            >
              {t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog 
        open={editAddressDialogOpen} 
        onClose={() => setEditAddressDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(handleUpdateAddress)}>
          <DialogTitle>{t('customers.editAddress')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  {...register('address_line1')}
                  label={t('customers.fields.addressLine1')}
                  fullWidth
                  error={!!errors.address_line1}
                  helperText={errors.address_line1?.message}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  {...register('address_line2')}
                  label={t('customers.fields.addressLine2')}
                  fullWidth
                  error={!!errors.address_line2}
                  helperText={errors.address_line2?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('city')}
                  label={t('customers.fields.city')}
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('state')}
                  label={t('customers.fields.state')}
                  fullWidth
                  error={!!errors.state}
                  helperText={errors.state?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('postal_code')}
                  label={t('customers.fields.postalCode')}
                  fullWidth
                  error={!!errors.postal_code}
                  helperText={errors.postal_code?.message}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('country')}
                  label={t('customers.fields.country')}
                  fullWidth
                  error={!!errors.country}
                  helperText={errors.country?.message}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register('is_default')}
                    />
                  }
                  label={t('customers.fields.isDefault')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditAddressDialogOpen(false);
              setEditingAddress(null);
              resetAddressForm();
            }}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={updatingAddress}
              startIcon={updatingAddress ? <CircularProgress size={20} /> : <Edit />}
            >
              {t('common.update')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default CustomerDetail;
